import type { RouteLocationNormalizedLoaded } from "vue-router";
import type { GuardResult, RouteContextInput, RouteContextInputLoose } from "../types/guards";

export function useRouteGuard() {
  const localePath = useLocalePath();
  const getRouteBaseName = useRouteBaseName();
  const router = useRouter();

  // Should not use useRoute here because it needs to be used in middlewares
  const currentRoute = computed(() => router.currentRoute.value);

  const rootRoute = computed(() => localePath("/"));

  const parseRoute = (route: RouteContextInputLoose) => {
    return localePath(route);
  };

  const resolveRouteContext = <
    T_Defaults extends Record<string, unknown> = Record<string, unknown>,
    T_Matches extends Record<string, string> = Record<string, string>,
    T_Query extends Record<string, MaybeTransform<unknown>> = Record<string, MaybeTransform<unknown>>,
    T_Params extends Record<string, MaybeTransform<unknown>> = Record<string, MaybeTransform<unknown>>,
  >(
    inputRoute: RouteContextInput,
    {
      defaults = {} as T_Defaults,
      matchPrefixes = {} as T_Matches,
      queryDefaults = {} as T_Query,
      paramsDefaults = {} as T_Params,
    }: {
      defaults?: T_Defaults;
      matchPrefixes?: T_Matches;
      queryDefaults?: T_Query;
      paramsDefaults?: T_Params;
    } = {},
  ) => {
    const route =
      typeof inputRoute === "string" ? (router.resolve(inputRoute) as RouteLocationNormalizedLoaded) : inputRoute;
    // remove locale from route name e.g 'setup-facilityId---es' to 'setup-facilityId'
    const normalizedName = getRouteBaseName(route)?.toString();

    const matches = Object.fromEntries(
      Object.entries(matchPrefixes).map(([key, prefix]) => [
        key,
        normalizedName?.startsWith(prefix) ? (normalizedName === prefix ? "strict" : true) : false,
      ]),
    ) as { [key in keyof T_Matches]: boolean | "strict" };

    const query = Object.fromEntries(
      Object.entries(queryDefaults).map(([key, maybeValue]) => [
        key,
        $ck.utils.resolveTransform(route.query[key], maybeValue),
      ]),
    ) as { [key in keyof T_Query]: MaybeReturnType<T_Query[key]> };

    const params = Object.fromEntries(
      Object.entries(paramsDefaults).map(([key, maybeValue]) => [
        key,
        $ck.utils.resolveTransform(route.params[key], maybeValue),
      ]),
    ) as { [key in keyof T_Params]: MaybeReturnType<T_Params[key]> };

    return {
      ...defaults,
      route,
      normalizedName,
      isRecognized: normalizedName !== undefined,
      localizedPath: localePath(route.path),
      isRedirectingPage: normalizedName === "redirecting",
      matches,
      query,
      params,
    };
  };

  const makeGuardResult = (
    route: RouteLocationNormalizedLoaded,
    action:
      | "continue"
      | "abort"
      | "root"
      | {
          redirect: RouteContextInputLoose;
        }
      | {
          replace: RouteContextInputLoose;
        },
  ): GuardResult => {
    if (action === "continue") {
      return { ok: true };
    }

    if (action === "abort") {
      return { ok: false, result: abortNavigation() ?? false };
    }

    if (action === "root") {
      return { ok: false, result: navigateTo(rootRoute.value) ?? false };
    }

    const targetRoute = parseRoute("redirect" in action ? action.redirect : action.replace);

    if (targetRoute === route.fullPath) {
      devConsole.warn("🚦 guard rule triggered: trying to navigate to the current route");
      return { ok: true };
    }

    const replace = typeof action === "object" && "replace" in action;

    return {
      ok: false,
      result:
        navigateTo(targetRoute, {
          replace,
        }) ?? false,
    };
  };

  return {
    rootRoute,
    currentRoute,
    parseRoute,
    resolveRouteContext,
    makeGuardResult,
  };
}
